'use client';

import { FC, useEffect, useState } from 'react';

import { CallOffOrderDetail } from '@ravago/shared/page-data/models/elements';
import { PermissionResources } from '@ravago/shared/page-data/models/features';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { DatePicker } from '@ravago/shared/radiance/components/input-field/components/DatePicker/DatePicker';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { parseISO, isWeekend } from 'date-fns';

import { useCallOffAgreementContext } from '../../../../context/call-off-agreement-context';
import { useFlyoutContext } from '../../../../hooks/use-flyout-context';
import { usePermission } from '../../../../hooks/use-permission';

interface Props {
   callOffOrderDetail: CallOffOrderDetail;
   usesAgreementApp: boolean;
   showDeliveryAddress?: boolean;
   onClick?: () => void;
}

export const CallOffOrderDeliveryRequest: FC<Props> = ({ callOffOrderDetail, showDeliveryAddress = true, usesAgreementApp, onClick }) => {
   const translate = useTranslations();
   const [quantity, setQuantity] = useState('');
   const [hasQuantityError, setHasQuantityError] = useState(false);
   const [additionalInfo, setAdditionalInfo] = useState('');
   const [deliveryDate, setDeliveryDate] = useState(DateUtils.getNextBusinessDay());
   const [quantityErrorMessage, setQuantityErrorMessage] = useState('');
   const [dateErrorMessage, setDateErrorMessage] = useState('');
   const { state: flyoutState, dispatch: flyoutDispatch } = useFlyoutContext();
   const { state: callOffAgreementState, dispatch: callOffAgreementDispatch } = useCallOffAgreementContext();
   const { hasPermission: canRequestDelivery, isLoading: requestDeliveryPermissionLoading } = usePermission(PermissionResources.REQUEST_DELIVERY);

   useEffect(() => {
      if (callOffAgreementState) {
         setQuantity(callOffAgreementState.quantity?.amount.toString() || '');
         setDeliveryDate(callOffAgreementState.deliveryDate || DateUtils.getNextBusinessDay());
         setAdditionalInfo(callOffAgreementState.additionalInfo || '');
      }
   }, [callOffAgreementState]);

   const handleNewQuantityChange = (value: string) => {
      const qty = Number.parseInt(value, 10);
      setQuantity(value);
      if (Number.isNaN(qty) || qty > callOffOrderDetail.remainingQuantity.amount) {
         setQuantityErrorMessage(translate('request-delivery.invalid-quantity'));
         setHasQuantityError(true);
         return;
      }
      callOffAgreementDispatch?.({
         type: 'set_call_off_quantity',
         value: {
            quantity: {
               amount: Number.parseInt(value, 10),
               unitOfMeasure: callOffOrderDetail.remainingQuantity.unitOfMeasure
            }
         }
      });
      setHasQuantityError(false);
   };

   const handleDeliveryDateChange = (value: string) => {
      const selectedDate = parseISO(value);
      const nextBusinessDate = new Date(DateUtils.getNextBusinessDay());

      if (isWeekend(selectedDate)) {
         setDateErrorMessage(translate('request-delivery.is-weekend'));
      } else if (selectedDate < nextBusinessDate) {
         setDateErrorMessage(translate('request-delivery.date-past'));
      } else {
         setDateErrorMessage('');
      }
      callOffAgreementDispatch?.({
         type: 'set_call_off_delivery_date',
         value: { deliveryDate: value }
      });
      setDeliveryDate(value);
   };

   const handleAdditionalInfoChange = (value: string) => {
      setAdditionalInfo(value);
      callOffAgreementDispatch?.({
         type: 'set_call_off_additional_info',
         value: { additionalInfo: value }
      });
   };

   const handleRequestDeliveryClick = () => {
      if (flyoutDispatch && callOffAgreementDispatch && canRequestDelivery) {
         callOffAgreementDispatch({
            type: 'set_call_off',
            value: {
               callOffOrderDetail,
               deliveryDate,
               quantity: {
                  amount: Number.parseInt(quantity, 10),
                  unitOfMeasure: callOffOrderDetail.remainingQuantity.unitOfMeasure
               },
               additionalInfo,
               usesAgreementApp
            }
         });
         if (flyoutState.idOpenDialog !== 'call-off-agreement') {
            flyoutDispatch({
               type: 'open_flyout',
               value: { flyoutType: 'call-off-agreement', title: 'Call off agreement' }
            });
         } else {
            onClick?.();
         }
      }
   };

   return (
      <Box direction="column" gap="md">
         <InputField
            type="number"
            label="Quantity"
            id="request-delivery-add-quantity"
            suffix={callOffOrderDetail.remainingQuantity.unitOfMeasure}
            value={quantity}
            disabled={!canRequestDelivery}
            min={1}
            max={999999999}
            error={hasQuantityError}
            errorMessage={quantityErrorMessage}
            onChange={handleNewQuantityChange}
         />
         <DatePicker
            id="request-delivery-date-picker"
            label={translate('request-delivery.delivery-date-label')}
            min={DateUtils.getNextBusinessDay()}
            disabled={!canRequestDelivery}
            error={!!dateErrorMessage}
            errorMessage={dateErrorMessage}
            value={deliveryDate}
            onChange={handleDeliveryDateChange}
         />
         <InputField
            label={translate('request-delivery.additional-information')}
            id="request-delivery-additional-info-input"
            maxLength={200}
            disabled={!canRequestDelivery}
            value={additionalInfo}
            onChange={handleAdditionalInfoChange}
         />
         {callOffOrderDetail.destinationAddress && showDeliveryAddress && (
            <Box gap="xs" direction="column">
               <Typography component="span" variant="body-highlight">
                  {translate('request-for-quotation.delivery')}
               </Typography>
               <Box direction="column" gap="none">
                  <Typography component="b" variant="body" color="strong">
                     {`${callOffOrderDetail.destinationAddress.line1}, ${callOffOrderDetail.destinationAddress.line2 || ''}`}
                  </Typography>
                  <Typography component="span" variant="body" color="strong">
                     {`${callOffOrderDetail.destinationAddress.city} ${callOffOrderDetail.destinationAddress.postCode}, ${callOffOrderDetail.destinationAddress.countryName}`}
                  </Typography>
               </Box>
            </Box>
         )}
         {!requestDeliveryPermissionLoading && (
            <>
               <Button
                  fullWidth
                  trailingIcon="long-arrow-right"
                  disabled={!quantity || !deliveryDate || !!dateErrorMessage || hasQuantityError || !canRequestDelivery}
                  variant={flyoutState.idOpenDialog !== 'call-off-agreement' ? 'primary' : 'secondary'}
                  onClick={handleRequestDeliveryClick}
               >
                  {translate('request-delivery.summary')}
               </Button>
               {!canRequestDelivery && <Typography>{translate('permissions.no-request-deliveries-permission')}</Typography>}
            </>
         )}
      </Box>
   );
};
